
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Heart, Rocket, HeartPulse, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { LocaleLink } from '@lib/i18n/exports';

/**
 * AnnouncementBar -- Thin promotional banner above the header.
 *
 * Shows Fundasister and Launchpad announcements on the landing page only.
 * Dismissible with localStorage persistence so it stays hidden per session.
 *
 * Placement: src/App.tsx, rendered before <Header> inside !isGazianExperience block.
 */

const DISMISS_KEY = 'amalq_announcement_dismissed';

type BadgeVariant = 'emerald' | 'amber' | 'sky';

interface AnnouncementBadgeConfig {
  id: string;
  labelKey: string;
  linkTo: string;
  variant: BadgeVariant;
}

const BADGES: AnnouncementBadgeConfig[] = [
  {
    id: 'bangrilla',
    labelKey: 'announcementBar.bangrilla',
    linkTo: '/bangrilla',
    variant: 'sky',
  },
  {
    id: 'fundasister',
    labelKey: 'announcementBar.fundasister',
    linkTo: '/fundasister',
    variant: 'emerald',
  },
  {
    id: 'launchpad',
    labelKey: 'announcementBar.launchpad',
    linkTo: '/launchpad',
    variant: 'amber',
  },
];

const variantStyles: Record<BadgeVariant, string> = {
  emerald:
    'border-emerald-200/60 bg-emerald-50/80 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100',
  amber:
    'border-amber-200/60 bg-amber-50/80 text-amber-700 hover:border-amber-300 hover:bg-amber-100',
  sky:
    'border-sky-200/60 bg-sky-50/80 text-sky-700 hover:border-sky-300 hover:bg-sky-100',
};

const variantIcons: Record<BadgeVariant, React.FC<{ className?: string }>> = {
  emerald: ({ className }) => <Heart size={10} className={className} />,
  amber: ({ className }) => <Rocket size={10} className={className} />,
  sky: ({ className }) => <HeartPulse size={10} className={className} />,
};

const chevronColor: Record<BadgeVariant, string> = {
  emerald: 'text-emerald-400',
  amber: 'text-amber-400',
  sky: 'text-sky-400',
};

const iconColor: Record<BadgeVariant, string> = {
  emerald: 'text-emerald-500',
  amber: 'text-amber-500',
  sky: 'text-sky-500',
};

const AnnouncementBar: React.FC = () => {
  const { t } = useTranslation('landing');
  const location = useLocation();
  const [dismissed, setDismissed] = useState(true); // default hidden to avoid flash

  // Only show on landing page (root or /:locale)
  const isLandingPage = /^\/?([a-z]{2}(-[A-Za-z]+)?)?\/?$/.test(location.pathname);

  useEffect(() => {
    // Check localStorage after mount to avoid SSR mismatch
    const stored = localStorage.getItem(DISMISS_KEY);
    if (!stored) {
      setDismissed(false);
    }
  }, []);

  const handleDismiss = (): void => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  if (!isLandingPage || dismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="relative z-50 border-b border-emerald-100/50 bg-linear-to-r from-emerald-50/60 via-white to-amber-50/40"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <div className="mx-auto flex max-w-[1320px] items-center justify-center gap-3 px-4 py-1.5 sm:px-6">
          {/* Badges */}
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            {BADGES.map((badge) => {
              const IconComponent = variantIcons[badge.variant];
              return (
                <LocaleLink
                  key={badge.id}
                  to={badge.linkTo}
                  className={`group inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-all duration-200 sm:text-xs ${variantStyles[badge.variant]}`}
                >
                  <IconComponent className={iconColor[badge.variant]} />
                  <span className="whitespace-nowrap">
                    {t(badge.labelKey)}
                  </span>
                  <ChevronRight
                    size={10}
                    className={`transition-transform group-hover:translate-x-0.5 rtl:rotate-180 group-hover:rtl:-translate-x-0.5 ${chevronColor[badge.variant]}`}
                  />
                </LocaleLink>
              );
            })}
          </div>

          {/* Dismiss button */}
          <button
            type="button"
            onClick={handleDismiss}
            className="ms-2 shrink-0 rounded-full p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600"
            aria-label={t('announcementBar.dismiss')}
          >
            <X size={14} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AnnouncementBar;
